import { useRef, useState } from "react";
import type { FormEvent } from "react";
import { api } from "./api";
import type {
  Catalog,
  Introduction,
  Need,
  NeedDraft,
  Session,
  Suggestions,
} from "./api";
import {
  Badge,
  Empty,
  Icon,
  PageTitle,
  dateTime,
  errorMessage,
} from "./components";

const statusLabels: Record<Introduction["status"], string> = {
  pending: "En attente",
  accepted: "Accord donné",
  declined: "Déclinée",
  withdrawn: "Retirée",
  expired: "Expirée",
};
export function RequestList({
  requests,
  personId,
  onRespond,
  busy = false,
}: {
  requests: Introduction[];
  personId: string;
  onRespond: (id: string, action: "accept" | "decline" | "withdraw") => void;
  busy?: boolean;
}) {
  if (!requests.length)
    return (
      <Empty icon="guild" title="Le prochain lien reste à créer.">
        Vos demandes envoyées et reçues se retrouveront ici.
      </Empty>
    );
  return (
    <div className="stack">
      {requests.map((request) => {
        const received = request.recipient_person_id === personId;
        return (
          <article className="request panel" key={request.id}>
            <div className="section-heading">
              <span className="eyebrow">
                {received ? "Demande reçue" : "Demande envoyée"}
              </span>
              <Badge tone={request.status === "accepted" ? "green" : ""}>
                {statusLabels[request.status] || request.status}
              </Badge>
            </div>
            <h3>{received ? request.sender_name : request.recipient_name}</h3>
            <p className="request-context">{request.context}</p>
            {request.status === "pending" && (
              <p className="fineprint">
                Valable jusqu’au {dateTime(request.expires_at)}.
              </p>
            )}
            {request.status === "accepted" && (
              <p className="note">
                La mise en relation est acceptée. L’organisation d’une rencontre
                et l’ajout aux contacts restent des étapes distinctes, à venir.
              </p>
            )}
            <div className="button-row">
              {received && request.status === "pending" && (
                <>
                  <button
                    className="button primary"
                    disabled={busy}
                    onClick={() => onRespond(request.id, "accept")}
                  >
                    Accepter
                    <Icon name="check" />
                  </button>
                  <button
                    className="button"
                    disabled={busy}
                    onClick={() => onRespond(request.id, "decline")}
                  >
                    Décliner
                  </button>
                </>
              )}
              {((!received && request.status === "pending") ||
                request.status === "accepted") && (
                <button
                  className="text-button"
                  disabled={busy}
                  onClick={() => onRespond(request.id, "withdraw")}
                >
                  {request.status === "accepted"
                    ? "Retirer mon accord"
                    : "Retirer ma demande"}
                </button>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}

export type GuildTab = "discover" | "requests" | "offers" | "contacts";

export function GuildScreen({
  tab,
  onTabChange,
  session,
  catalog,
  onProfile,
  draft,
  onDraft,
  needs,
  requests,
  availability,
  onAuth,
  onRefresh,
  onAvailability,
  onRespond,
  onNotify,
  busy,
}: {
  tab: GuildTab;
  onTabChange: (tab: GuildTab) => void;
  session: Session;
  catalog: Catalog;
  onProfile: () => void;
  draft: NeedDraft;
  onDraft: (draft: NeedDraft) => void;
  needs: Need[];
  requests: Introduction[];
  availability: string | null;
  onAuth: () => void;
  onRefresh: () => Promise<void>;
  onAvailability: (available: boolean) => void;
  onRespond: (id: string, action: "accept" | "decline" | "withdraw") => void;
  onNotify: (message: string) => void;
  busy: boolean;
}) {
  const [skillsInput, setSkillsInput] = useState(draft.help_terms.join(", "));
  const [results, setResults] = useState<Suggestions | null>(null);
  const [selectedNeed, setSelectedNeed] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [error, setError] = useState("");
  const requestKeys = useRef(new Map<string, string>());
  const received = requests.filter(
    (r) =>
      r.recipient_person_id === session.person?.id && r.status === "pending",
  ).length;
  async function search(needId?: string) {
    if (!session.person) {
      onAuth();
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    try {
      const id = needId ?? (await api.createNeed(draft)).need.id;
      setSelectedNeed(id);
      await onRefresh();
      if (!needId && draft.visibility_scope === "private") {
        onNotify(
          "Votre besoin privé est enregistré. Il ne déclenche aucune recherche ni mise en relation.",
        );
        return;
      }
      setResults(await api.suggestions(id));
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    void search();
  }
  async function introduce(personId: string) {
    if (!selectedNeed) return;
    setSending(personId);
    setError("");
    const identity = `${selectedNeed}:${personId}`;
    if (!requestKeys.current.has(identity))
      requestKeys.current.set(identity, crypto.randomUUID());
    try {
      await api.introduce(
        selectedNeed,
        personId,
        requestKeys.current.get(identity)!,
      );
      await onRefresh();
      onNotify("Demande envoyée. La personne choisira librement d’y répondre.");
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      setSending(null);
    }
  }
  return (
    <>
      <PageTitle eyebrow="Nos talents se rencontrent" title="La Guilde">
        Un besoin concret. Une aide à proposer. Le bon point de départ pour
        faire ensemble.
      </PageTitle>
      {session.person && (
        <section
          className={`availability ${availability ? "is-available" : ""}`}
        >
          <div>
            <span className="availability-dot" />
            <strong>
              {availability ? "Disponible pour un échange" : "À votre rythme"}
            </strong>
            <p>
              {availability
                ? `Jusqu’au ${dateTime(availability)}`
                : "Ouvrir Avalon ne vous rend pas disponible."}
            </p>
          </div>
          <button
            className="text-button"
            disabled={busy}
            onClick={() => onAvailability(!availability)}
          >
            {availability ? "Me retirer" : "Disponible 1 h"}
          </button>
        </section>
      )}
      <div className="segments segments-four" aria-label="Vues de la Guilde">
        <button
          aria-pressed={tab === "discover"}
          onClick={() => onTabChange("discover")}
        >
          Trouver un lien
        </button>
        <button
          aria-pressed={tab === "requests"}
          onClick={() => onTabChange("requests")}
        >
          Mes demandes
          {received > 0 && <span className="count">{received}</span>}
        </button>
        <button
          aria-pressed={tab === "offers"}
          onClick={() => onTabChange("offers")}
        >
          Mes apports
        </button>
        <button
          aria-pressed={tab === "contacts"}
          onClick={() => onTabChange("contacts")}
        >
          Contacts
        </button>
      </div>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {tab === "offers" ? (
        <section className="panel">
          <div className="section-heading">
            <h2>Ce que je peux apporter</h2>
            <Icon name="spark" />
          </div>
          <p className="muted">
            Vos apports décrivent l’aide que vous proposez. Ils sont distincts
            des besoins que vous exprimez pour vous-même.
          </p>
          {session.profile ? (
            <>
              {session.profile.offered_skills.length ? (
                <div className="tag-row">
                  {session.profile.offered_skills.map((skill) => (
                    <Badge key={skill}>{skill}</Badge>
                  ))}
                </div>
              ) : (
                <Empty icon="guild" title="Votre savoir-faire a sa place.">
                  Ajoutez quelques compétences à votre profil pour faire
                  découvrir ce que vous aimez partager.
                </Empty>
              )}
              <p className="note">
                {session.profile.visibility === "members"
                  ? "Ces apports peuvent être utilisés par Ava pour répondre à des besoins autorisés de membres."
                  : "Votre profil est privé : ces apports ne sont pas proposés aux autres membres."}
              </p>
              <button className="button primary" onClick={onProfile}>
                Modifier mes apports <Icon name="arrow" />
              </button>
            </>
          ) : (
            <button className="button primary" onClick={onAuth}>
              Créer mon compte pour proposer mon aide
            </button>
          )}
        </section>
      ) : tab === "contacts" ? (
        <>
          <section className="panel">
            <h2>Mes contacts</h2>
            <Empty icon="guild" title="Les contacts durables arrivent.">
              L’ajout réciproque aux contacts n’est pas encore disponible.
              Aucune demande acceptée ne devient automatiquement un contact.
            </Empty>
          </section>
          <section className="panel">
            <h2>De l’accord à la rencontre</h2>
            <ol className="feature-list">
              <li>Accepter une mise en relation pour choisir de poursuivre.</li>
              <li>
                Convenir ensemble d’un rendez-vous, sur place ou à distance.
              </li>
              <li>Demander séparément à garder le contact.</li>
            </ol>
            <p className="note">
              L’organisation d’un rendez-vous et l’ajout réciproque aux contacts
              seront proposés dans une prochaine version. Aucun espace n’est
              réservé automatiquement.
            </p>
            <button className="button" onClick={() => onTabChange("requests")}>
              Retrouver mes demandes <Icon name="arrow" />
            </button>
          </section>
        </>
      ) : tab === "requests" ? (
        session.person ? (
          <>
            <RequestList
              requests={requests}
              personId={session.person.id}
              onRespond={onRespond}
              busy={busy}
            />
            <p className="fineprint">
              Les contacts durables et l’organisation de rencontres seront
              proposés dans une prochaine version.
            </p>
          </>
        ) : (
          <section className="panel">
            <Empty icon="guild" title="Retrouvez vos liens.">
              Connectez-vous pour voir les demandes qui vous concernent.
            </Empty>
            <button className="button primary full" onClick={onAuth}>
              Rejoindre la Guilde
            </button>
          </section>
        )
      ) : (
        <>
          <form className="panel form-stack" onSubmit={submit}>
            <div className="section-heading">
              <h2>De quoi avez-vous besoin ?</h2>
              <Icon name="spark" />
            </div>
            <label>
              L’aide recherchée
              <input
                required
                maxLength={500}
                value={skillsInput}
                onChange={(e) => {
                  setSkillsInput(e.target.value);
                  onDraft({
                    ...draft,
                    help_terms: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean),
                  });
                }}
                placeholder="Par exemple : design, photographie"
              />
              <span className="field-help">
                Quelques compétences, séparées par une virgule.
              </span>
            </label>
            <label>
              Un peu de contexte
              <textarea
                required
                minLength={3}
                maxLength={800}
                value={draft.context}
                onChange={(e) => onDraft({ ...draft, context: e.target.value })}
                placeholder="Je cherche un regard sur la présentation de mon projet…"
              />
            </label>
            <div className="form-columns">
              <label>
                Comment échanger ?
                <select
                  value={draft.distance_preference}
                  onChange={(e) =>
                    onDraft({
                      ...draft,
                      distance_preference: e.target
                        .value as NeedDraft["distance_preference"],
                    })
                  }
                >
                  <option value="any">Sur place ou à distance</option>
                  <option value="remote">À distance</option>
                  <option value="onsite">Sur place</option>
                </select>
              </label>
              <label>
                Un lieu de préférence
                <select
                  value={draft.place_id || ""}
                  onChange={(e) =>
                    onDraft({ ...draft, place_id: e.target.value || null })
                  }
                >
                  <option value="">Sans préférence</option>
                  {catalog.places.map((place) => (
                    <option key={place.id} value={place.id}>
                      {place.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <label>
              Visibilité de ce besoin
              <select
                value={draft.visibility_scope}
                onChange={(e) =>
                  onDraft({
                    ...draft,
                    visibility_scope: e.target
                      .value as NeedDraft["visibility_scope"],
                  })
                }
              >
                <option value="members">Membres autorisés</option>
                <option value="private">Privé, pour préparer mon besoin</option>
              </select>
            </label>
            <label className="check-label">
              <input
                type="checkbox"
                checked={draft.require_available}
                onChange={(e) =>
                  onDraft({ ...draft, require_available: e.target.checked })
                }
              />
              Uniquement les personnes disponibles maintenant
            </label>
            <p className="fineprint">
              Ava s’appuie sur les compétences proposées et les informations
              autorisées. Votre besoin sera transmis à la personne si vous lui
              envoyez une demande.
            </p>
            <button
              className="button primary full"
              disabled={loading || !draft.help_terms.length}
            >
              {loading
                ? "Enregistrement et recherche…"
                : session.person
                  ? draft.visibility_scope === "private"
                    ? "Enregistrer mon besoin privé"
                    : "Enregistrer et trouver des personnes"
                  : "Créer mon compte pour continuer"}
              <Icon name="arrow" />
            </button>
          </form>
          {results && (
            <section className="results" aria-live="polite">
              <div className="section-heading">
                <h2>Des points communs</h2>
                <Badge>Ava</Badge>
              </div>
              {results.status === "unavailable" ? (
                <Empty icon="clock" title="Ava est momentanément indisponible.">
                  Votre besoin est enregistré. Vous pourrez relancer la
                  recherche.
                </Empty>
              ) : !results.suggestions.length ? (
                <Empty icon="guild" title="Pas encore de lien pertinent.">
                  Vous pouvez élargir vos préférences ou réessayer plus tard.
                  Ava n’a pas trouvé de compétence proposée qui corresponde.
                </Empty>
              ) : (
                <div className="stack">
                  {results.suggestions.map((suggestion) => {
                    const sent = requests.some(
                      (r) =>
                        r.need_id === selectedNeed &&
                        r.recipient_person_id === suggestion.person_id,
                    );
                    return (
                      <article
                        className="panel suggestion"
                        key={suggestion.person_id}
                      >
                        <div className="profile-summary">
                          <span className="avatar">
                            {suggestion.display_name.slice(0, 1)}
                          </span>
                          <div>
                            <h3>{suggestion.display_name}</h3>
                            <span className="fineprint">
                              Compétences déclarées
                            </span>
                          </div>
                        </div>
                        <div className="tags">
                          {suggestion.matched_skills.map((skill) => (
                            <Badge key={skill} tone="purple">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                        <p className="reason">
                          <Icon name="spark" size={16} />
                          {suggestion.reason}
                        </p>
                        <button
                          className="button full"
                          disabled={sent || sending !== null}
                          onClick={() => introduce(suggestion.person_id)}
                        >
                          {sent
                            ? "Demande enregistrée"
                            : sending === suggestion.person_id
                              ? "Envoi…"
                              : "Demander une mise en relation"}
                          <Icon name={sent ? "check" : "arrow"} />
                        </button>
                      </article>
                    );
                  })}
                </div>
              )}
              {results.status === "partial" && (
                <p className="note">
                  Les résultats sont partiels. Certaines sources n’ont pas pu
                  être consultées.
                </p>
              )}
              {results.limitations?.map((limitation, index) => (
                <p className="fineprint" key={index}>
                  {limitation}
                </p>
              ))}
            </section>
          )}
          {needs.length > 0 && (
            <section>
              <div className="section-heading">
                <h2>Mes besoins enregistrés</h2>
                <span className="muted">{needs.length}</span>
              </div>
              <div className="panel saved-needs">
                {needs.map((need) => (
                  <div key={need.id}>
                    <div>
                      <h3>{need.help_terms.join(", ")}</h3>
                      <p className="muted">{need.context}</p>
                    </div>
                    <button
                      className="text-button"
                      disabled={
                        loading ||
                        need.visibility_scope === "private" ||
                        need.status === "closed"
                      }
                      onClick={() => search(need.id)}
                      aria-label={`Rechercher pour ${need.help_terms.join(", ")}`}
                    >
                      <Icon name="refresh" />
                      <span>Rechercher</span>
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
